import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { customerAuthAPI, checkoutAPI, couponAPI, IMAGE_BASE } from '../../services/api';
import { MapPin, CreditCard, ShoppingBag, ChevronRight, Plus, ArrowLeft, X, Tag, Truck, Building2, Copy, Check, CheckCircle, Package, Home } from 'lucide-react';
import { legalPageAPI } from '../../services/api';

export default function Checkout() {
    const { cart, fetchCart } = useCart();
    const { customer } = useCustomerAuth();
    const navigate = useNavigate();

    const [step, setStep] = useState(1); // 1: address, 2: review, 3: payment
    const [addresses, setAddresses] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [showNewAddr, setShowNewAddr] = useState(false);
    const [addrForm, setAddrForm] = useState({ title: 'Adresim', firstName: '', lastName: '', phone: '', city: '', district: '', neighborhood: '', address: '', zipCode: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [paymentHtml, setPaymentHtml] = useState('');
    const paymentRef = useRef(null);
    const [paymentMethod, setPaymentMethod] = useState('credit_card');
    const [copiedIban, setCopiedIban] = useState(false);
    const [confirmedOrder, setConfirmedOrder] = useState(null);

    // Invoice type
    const [invoiceType, setInvoiceType] = useState('individual');
    const [corporateInfo, setCorporateInfo] = useState({ companyName: '', taxOffice: '', taxNumber: '' });

    // Coupon
    const [couponCode, setCouponCode] = useState('');
    const [couponResult, setCouponResult] = useState(null);
    const [couponLoading, setCouponLoading] = useState(false);
    const [couponError, setCouponError] = useState('');

    // TC Kimlik No (kapıda ödeme için)
    const [identityNumber, setIdentityNumber] = useState('');

    // Agreement states
    const [agreements, setAgreements] = useState({ mesafeli: false, onBilgilendirme: false, kvkk: false });
    const [agreementModal, setAgreementModal] = useState(null); // { title, content }
    const [agreementLoading, setAgreementLoading] = useState(false);

    useEffect(() => {
        if (!customer) {
            navigate('/giris', { state: { from: '/checkout' } });
            return;
        }
        if (!customer.emailVerified) {
            navigate('/eposta-dogrula');
            return;
        }
        if (cart.items.length === 0 && !confirmedOrder) {
            navigate('/urunler');
            return;
        }
        loadAddresses();
    }, [customer, cart.items.length, navigate, confirmedOrder]);

    const loadAddresses = async () => {
        try {
            const res = await customerAuthAPI.getAddresses();
            setAddresses(res.data);
            const defaultAddr = res.data.find(a => a.isDefault) || res.data[0];
            if (defaultAddr) setSelectedAddress(defaultAddr);
        } catch (e) { console.error(e); }
    };

    const handleNewAddress = async () => {
        if (!addrForm.firstName || !addrForm.lastName || !addrForm.phone || !addrForm.city || !addrForm.district || !addrForm.address) {
            setError('Lütfen tüm zorunlu alanları doldurun');
            return;
        }
        try {
            const res = await customerAuthAPI.createAddress(addrForm);
            setAddresses(prev => [...prev, res.data]);
            setSelectedAddress(res.data);
            setShowNewAddr(false);
            setError('');
        } catch (e) {
            setError(e.response?.data?.error || 'Adres eklenemedi');
        }
    };

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) return;
        setCouponLoading(true);
        setCouponError('');
        setCouponResult(null);
        try {
            const res = await couponAPI.validate({ code: couponCode.trim(), cartTotal: cart.totalAmount });
            setCouponResult(res.data);
        } catch (e) {
            setCouponError(e.response?.data?.error || 'Kupon doğrulanamadı');
        }
        setCouponLoading(false);
    };

    const handleRemoveCoupon = () => {
        setCouponCode('');
        setCouponResult(null);
        setCouponError('');
    };

    const handlePayment = async () => {
        if (!selectedAddress) {
            setError('Lütfen teslimat adresi seçin');
            return;
        }
        setLoading(true);
        setError('');

        const basePayload = {
            shippingAddressData: selectedAddress,
            billingAddressData: selectedAddress,
            invoiceType,
            couponCode: couponResult ? couponResult.code : undefined,
            ...(invoiceType === 'corporate' ? {
                companyName: corporateInfo.companyName,
                taxOffice: corporateInfo.taxOffice,
                taxNumber: corporateInfo.taxNumber
            } : {})
        };

        try {
            if (paymentMethod === 'credit_card') {
                const res = await checkoutAPI.initPayment(basePayload);
                setPaymentHtml(res.data.checkoutFormContent);
                setStep(3);
            } else {
                const method = paymentMethod === 'cash_on_delivery' ? 'cash_on_delivery' : 'bank_transfer';
                const res = await checkoutAPI.placeOrder({
                    ...basePayload,
                    paymentMethod: method,
                    ...(method === 'cash_on_delivery' ? { identityNumber } : {})
                });
                await fetchCart();
                setConfirmedOrder({ orderNumber: res.data.orderNumber, paymentMethod: method });
                setStep(3);
            }
        } catch (e) {
            setError(e.response?.data?.error || 'Ödeme işlemi başarısız');
        }
        setLoading(false);
    };

    const copyIban = (iban) => {
        navigator.clipboard.writeText(iban.replace(/\s/g, ''));
        setCopiedIban(true);
        setTimeout(() => setCopiedIban(false), 2000);
    };

    // Render iyzico payment form
    useEffect(() => {
        if (paymentHtml && paymentRef.current) {
            paymentRef.current.innerHTML = paymentHtml;
            // Execute scripts in the HTML
            const scripts = paymentRef.current.querySelectorAll('script');
            scripts.forEach(script => {
                const newScript = document.createElement('script');
                if (script.src) {
                    newScript.src = script.src;
                } else {
                    newScript.textContent = script.textContent;
                }
                document.body.appendChild(newScript);
            });
        }
    }, [paymentHtml]);

    const openAgreement = async (slug, title) => {
        setAgreementLoading(true);
        try {
            const res = await legalPageAPI.getBySlug(slug);
            setAgreementModal({ title: res.data.title, content: res.data.content });
        } catch {
            setAgreementModal({ title, content: '<p>İçerik yüklenemedi.</p>' });
        }
        setAgreementLoading(false);
    };

    const allAgreed = agreements.mesafeli && agreements.onBilgilendirme && agreements.kvkk;
    const corporateValid = invoiceType === 'individual' || (corporateInfo.companyName && corporateInfo.taxOffice && corporateInfo.taxNumber);
    const identityValid = paymentMethod !== 'cash_on_delivery' || /^\d{11}$/.test(identityNumber);

    const formatPrice = (p) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(p);

    if (!customer) return null;

    return (
        <div className="checkout-page">
            <div className="checkout-container">
                <div className="checkout-header">
                    <Link to="/urunler" className="checkout-back"><ArrowLeft size={18} /> Alışverişe Dön</Link>
                    <h1>Sipariş Tamamla</h1>
                </div>

                {/* Steps indicator */}
                <div className="checkout-steps">
                    <div className={`checkout-step ${step >= 1 ? 'active' : ''}`}><MapPin size={18} /><span>Adres</span></div>
                    <ChevronRight size={16} />
                    <div className={`checkout-step ${step >= 2 ? 'active' : ''}`}><ShoppingBag size={18} /><span>Özet</span></div>
                    <ChevronRight size={16} />
                    <div className={`checkout-step ${step >= 3 ? 'active' : ''}`}><CreditCard size={18} /><span>Ödeme</span></div>
                </div>

                {error && <div className="checkout-error">{error}</div>}

                <div className={`checkout-grid${confirmedOrder ? ' checkout-grid-full' : ''}`}>
                    <div className="checkout-main">
                        {/* Step 1: Address */}
                        {step === 1 && (
                            <div className="checkout-section">
                                <h2><MapPin size={20} /> Teslimat Adresi</h2>
                                {addresses.length > 0 && (
                                    <div className="checkout-addresses">
                                        {addresses.map(addr => (
                                            <div
                                                key={addr.id}
                                                className={`checkout-addr-card ${selectedAddress?.id === addr.id ? 'selected' : ''}`}
                                                onClick={() => setSelectedAddress(addr)}
                                            >
                                                <strong>{addr.title}</strong>
                                                <p>{addr.firstName} {addr.lastName}</p>
                                                <p>{addr.address}</p>
                                                <p>{addr.district}, {addr.city}</p>
                                                <p>{addr.phone}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <button className="btn btn-secondary" onClick={() => setShowNewAddr(!showNewAddr)} style={{marginTop:'1rem'}}>
                                    <Plus size={16} /> Yeni Adres Ekle
                                </button>

                                {showNewAddr && (
                                    <div className="checkout-new-addr">
                                        <div className="checkout-form-grid">
                                            <input placeholder="Adres başlığı" value={addrForm.title} onChange={e => setAddrForm({...addrForm, title: e.target.value})} />
                                            <input placeholder="Ad *" value={addrForm.firstName} onChange={e => setAddrForm({...addrForm, firstName: e.target.value})} />
                                            <input placeholder="Soyad *" value={addrForm.lastName} onChange={e => setAddrForm({...addrForm, lastName: e.target.value})} />
                                            <input placeholder="Telefon *" value={addrForm.phone} onChange={e => setAddrForm({...addrForm, phone: e.target.value})} />
                                            <input placeholder="İl *" value={addrForm.city} onChange={e => setAddrForm({...addrForm, city: e.target.value})} />
                                            <input placeholder="İlçe *" value={addrForm.district} onChange={e => setAddrForm({...addrForm, district: e.target.value})} />
                                            <input placeholder="Mahalle" value={addrForm.neighborhood} onChange={e => setAddrForm({...addrForm, neighborhood: e.target.value})} />
                                            <textarea placeholder="Açık adres *" value={addrForm.address} onChange={e => setAddrForm({...addrForm, address: e.target.value})} style={{gridColumn:'1/-1'}} />
                                            <input placeholder="Posta kodu" value={addrForm.zipCode} onChange={e => setAddrForm({...addrForm, zipCode: e.target.value})} />
                                        </div>
                                        <button className="btn btn-primary" onClick={handleNewAddress} style={{marginTop:'1rem'}}>Adresi Kaydet</button>
                                    </div>
                                )}

                                <div className="checkout-nav">
                                    <button className="btn btn-primary" onClick={() => { if (!selectedAddress) { setError('Lütfen adres seçin'); return; } setError(''); setStep(2); }} disabled={!selectedAddress}>
                                        Devam Et <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 2: Review */}
                        {step === 2 && (
                            <div className="checkout-section">
                                <h2><ShoppingBag size={20} /> Sipariş Özeti</h2>
                                <div className="checkout-items">
                                    {cart.items.map(item => (
                                        <div key={item.id} className="checkout-item">
                                            <div className="checkout-item-img">
                                                {item.image ? <img src={item.image.startsWith('http') ? item.image : IMAGE_BASE + item.image} alt="" /> : <ShoppingBag size={20} />}
                                            </div>
                                            <div className="checkout-item-info">
                                                <strong>{item.productName}</strong>
                                                {item.variantInfo && <span>{item.variantInfo}</span>}
                                                <span>{item.quantity} x {formatPrice(item.price)}</span>
                                            </div>
                                            <div className="checkout-item-total">{formatPrice(item.lineTotal)}</div>
                                        </div>
                                    ))}
                                </div>

                                <div className="checkout-addr-summary">
                                    <h3>Teslimat Adresi</h3>
                                    <p><strong>{selectedAddress?.firstName} {selectedAddress?.lastName}</strong></p>
                                    <p>{selectedAddress?.address}</p>
                                    <p>{selectedAddress?.district}, {selectedAddress?.city}</p>
                                    <p>{selectedAddress?.phone}</p>
                                    <button className="btn btn-secondary btn-sm" onClick={() => setStep(1)}>Değiştir</button>
                                </div>

                                {/* Kupon Kodu */}
                                <div className="checkout-coupon-section">
                                    <h3><Tag size={16} /> Kupon Kodu</h3>
                                    {couponResult ? (
                                        <div className="checkout-coupon-applied">
                                            <span className="checkout-coupon-badge">{couponResult.code}</span>
                                            <span className="checkout-coupon-discount">-{formatPrice(couponResult.discount)} indirim</span>
                                            <button className="checkout-coupon-remove" onClick={handleRemoveCoupon}><X size={14} /></button>
                                        </div>
                                    ) : (
                                        <div className="checkout-coupon-input">
                                            <input type="text" placeholder="Kupon kodunuzu girin" value={couponCode}
                                                onChange={e => setCouponCode(e.target.value.toUpperCase())}
                                                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleApplyCoupon())} />
                                            <button className="btn btn-secondary" onClick={handleApplyCoupon} disabled={couponLoading || !couponCode.trim()}>
                                                {couponLoading ? '...' : 'Uygula'}
                                            </button>
                                        </div>
                                    )}
                                    {couponError && <p className="checkout-coupon-error">{couponError}</p>}
                                </div>

                                {/* Fatura Tipi */}
                                <div className="checkout-billing-type">
                                    <h3>Fatura Bilgileri</h3>
                                    <div className="checkout-billing-tabs">
                                        <button className={`checkout-billing-tab ${invoiceType === 'individual' ? 'active' : ''}`} onClick={() => setInvoiceType('individual')}>
                                            Bireysel
                                        </button>
                                        <button className={`checkout-billing-tab ${invoiceType === 'corporate' ? 'active' : ''}`} onClick={() => setInvoiceType('corporate')}>
                                            Kurumsal
                                        </button>
                                    </div>
                                    {invoiceType === 'corporate' && (
                                        <div className="checkout-corporate-fields">
                                            <input placeholder="Firma Adı *" value={corporateInfo.companyName} onChange={e => setCorporateInfo({...corporateInfo, companyName: e.target.value})} />
                                            <input placeholder="Vergi Dairesi *" value={corporateInfo.taxOffice} onChange={e => setCorporateInfo({...corporateInfo, taxOffice: e.target.value})} />
                                            <input placeholder="Vergi Numarası *" value={corporateInfo.taxNumber} onChange={e => setCorporateInfo({...corporateInfo, taxNumber: e.target.value})} />
                                        </div>
                                    )}
                                    {invoiceType === 'corporate' && !corporateValid && (
                                        <p className="checkout-agreement-warn">Kurumsal fatura için tüm alanları doldurunuz.</p>
                                    )}
                                </div>

                                {/* Sözleşme Onayları */}
                                <div className="checkout-agreements">
                                    <h3>Sözleşme ve Onaylar</h3>
                                    <label className="checkout-agreement-row">
                                        <input type="checkbox" checked={agreements.onBilgilendirme} onChange={e => setAgreements(p => ({...p, onBilgilendirme: e.target.checked}))} />
                                        <span>
                                            <button type="button" className="agreement-link" onClick={() => openAgreement('on-bilgilendirme-formu', 'Ön Bilgilendirme Formu')}>Ön Bilgilendirme Formu</button>'nu okudum, onaylıyorum.
                                        </span>
                                    </label>
                                    <label className="checkout-agreement-row">
                                        <input type="checkbox" checked={agreements.mesafeli} onChange={e => setAgreements(p => ({...p, mesafeli: e.target.checked}))} />
                                        <span>
                                            <button type="button" className="agreement-link" onClick={() => openAgreement('mesafeli-satis-sozlesmesi', 'Mesafeli Satış Sözleşmesi')}>Mesafeli Satış Sözleşmesi</button>'ni okudum, onaylıyorum.
                                        </span>
                                    </label>
                                    <label className="checkout-agreement-row">
                                        <input type="checkbox" checked={agreements.kvkk} onChange={e => setAgreements(p => ({...p, kvkk: e.target.checked}))} />
                                        <span>
                                            <button type="button" className="agreement-link" onClick={() => openAgreement('kvkk-aydinlatma-metni', 'KVKK Aydınlatma Metni')}>KVKK Aydınlatma Metni</button>'ni okudum, onaylıyorum.
                                        </span>
                                    </label>
                                    {!allAgreed && <p className="checkout-agreement-warn">Devam etmek için tüm sözleşmeleri onaylamanız gerekmektedir.</p>}
                                </div>

                                {/* Ödeme Yöntemi */}
                                <div className="checkout-payment-method">
                                    <h3>Ödeme Yöntemi</h3>
                                    <div className="checkout-payment-options">
                                        <label className={`checkout-payment-option ${paymentMethod === 'credit_card' ? 'selected' : ''}`}>
                                            <input type="radio" name="paymentMethod" value="credit_card" checked={paymentMethod === 'credit_card'} onChange={e => setPaymentMethod(e.target.value)} />
                                            <CreditCard size={20} />
                                            <div>
                                                <strong>Kredi Kartı / Banka Kartı</strong>
                                                <span>iyzico güvencesiyle güvenli ödeme</span>
                                            </div>
                                        </label>
                                        <label className={`checkout-payment-option ${paymentMethod === 'cash_on_delivery' ? 'selected' : ''}`}>
                                            <input type="radio" name="paymentMethod" value="cash_on_delivery" checked={paymentMethod === 'cash_on_delivery'} onChange={e => setPaymentMethod(e.target.value)} />
                                            <Truck size={20} />
                                            <div>
                                                <strong>Kapıda Ödeme</strong>
                                                <span>Teslimat sırasında nakit veya kart ile ödeme</span>
                                            </div>
                                        </label>
                                        <label className={`checkout-payment-option ${paymentMethod === 'bank_transfer' ? 'selected' : ''}`}>
                                            <input type="radio" name="paymentMethod" value="bank_transfer" checked={paymentMethod === 'bank_transfer'} onChange={e => setPaymentMethod(e.target.value)} />
                                            <Building2 size={20} />
                                            <div>
                                                <strong>Havale / EFT</strong>
                                                <span>Banka havalesi ile ödeme</span>
                                            </div>
                                        </label>
                                    </div>

                                    {paymentMethod === 'cash_on_delivery' && (
                                        <div className="checkout-identity-section">
                                            <label className="checkout-identity-label">TC Kimlik No <span style={{color:'var(--danger)'}}>*</span></label>
                                            <input
                                                type="text"
                                                className="checkout-identity-input"
                                                placeholder="11 haneli TC Kimlik Numaranız"
                                                value={identityNumber}
                                                onChange={e => {
                                                    const val = e.target.value.replace(/\D/g, '').slice(0, 11);
                                                    setIdentityNumber(val);
                                                }}
                                                maxLength={11}
                                            />
                                            {identityNumber && !/^\d{11}$/.test(identityNumber) && (
                                                <p className="checkout-identity-warn">TC Kimlik No 11 haneli olmalıdır</p>
                                            )}
                                        </div>
                                    )}

                                    {paymentMethod === 'bank_transfer' && (
                                        <div className="checkout-bank-info">
                                            <p className="checkout-bank-note">Sipariş oluşturulduktan sonra aşağıdaki hesaba havale/EFT yapmanız gerekmektedir. Ödeme onayı sonrası siparişiniz hazırlanacaktır.</p>
                                            <div className="checkout-bank-detail">
                                                <div className="checkout-bank-row"><span>Banka:</span><strong>Ziraat Bankası</strong></div>
                                                <div className="checkout-bank-row"><span>Hesap Sahibi:</span><strong>Atölye Kirpi</strong></div>
                                                <div className="checkout-bank-row">
                                                    <span>IBAN:</span>
                                                    <strong>TR00 0000 0000 0000 0000 0000 00</strong>
                                                    <button type="button" className="checkout-copy-btn" onClick={() => copyIban('TR00 0000 0000 0000 0000 0000 00')}>
                                                        {copiedIban ? <Check size={14} /> : <Copy size={14} />}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="checkout-nav">
                                    <button className="btn btn-secondary" onClick={() => setStep(1)}><ArrowLeft size={16} /> Geri</button>
                                    <button className="btn btn-primary" onClick={handlePayment} disabled={loading || !allAgreed || !corporateValid || !identityValid}>
                                        {loading ? 'Yükleniyor...' : paymentMethod === 'credit_card' ? 'Ödemeye Geç' : 'Siparişi Onayla'} {paymentMethod === 'credit_card' ? <CreditCard size={16} /> : <ChevronRight size={16} />}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Payment / Confirmation */}
                        {step === 3 && !confirmedOrder && (
                            <div className="checkout-section">
                                <h2><CreditCard size={20} /> Ödeme</h2>
                                <div ref={paymentRef} id="iyzipay-checkout-form" className="checkout-payment-form" />
                            </div>
                        )}

                        {step === 3 && confirmedOrder && (
                            <div className="checkout-section checkout-confirmation">
                                <div className="checkout-confirm-icon">
                                    <CheckCircle size={72} color="var(--success)" strokeWidth={1.5} />
                                </div>
                                <h2>Siparişiniz Onaylandı!</h2>
                                <p className="checkout-confirm-order-no">Sipariş Numaranız: <strong>#{confirmedOrder.orderNumber}</strong></p>

                                {confirmedOrder.paymentMethod === 'cash_on_delivery' && (
                                    <div className="checkout-confirm-info">
                                        <Truck size={22} />
                                        <p>Kapıda ödeme seçeneğini tercih ettiniz. Siparişiniz hazırlanarak kargoya verilecektir. Ödemeyi teslimat sırasında nakit veya kart ile yapabilirsiniz.</p>
                                    </div>
                                )}

                                {confirmedOrder.paymentMethod === 'bank_transfer' && (
                                    <div className="checkout-confirm-info">
                                        <Building2 size={22} />
                                        <p>Havale/EFT ile ödeme seçeneğini tercih ettiniz. Aşağıdaki hesaba ödemenizi yaptıktan sonra siparişiniz onaylanacaktır.</p>
                                        <div className="checkout-confirm-bank">
                                            <div className="checkout-bank-row"><span>Banka:</span><strong>Ziraat Bankası</strong></div>
                                            <div className="checkout-bank-row"><span>Hesap Sahibi:</span><strong>Atölye Kirpi</strong></div>
                                            <div className="checkout-bank-row">
                                                <span>IBAN:</span>
                                                <strong>TR00 0000 0000 0000 0000 0000 00</strong>
                                                <button type="button" className="checkout-copy-btn" onClick={() => copyIban('TR00 0000 0000 0000 0000 0000 00')}>
                                                    {copiedIban ? <Check size={14} /> : <Copy size={14} />}
                                                </button>
                                            </div>
                                            <p className="checkout-confirm-bank-note">Açıklama kısmına sipariş numaranızı (<strong>#{confirmedOrder.orderNumber}</strong>) yazmayı unutmayın.</p>
                                        </div>
                                    </div>
                                )}

                                <div className="checkout-confirm-actions">
                                    <Link to="/hesabim" className="btn btn-primary"><Package size={18} /> Siparişlerim</Link>
                                    <Link to="/" className="btn btn-secondary"><Home size={18} /> Ana Sayfa</Link>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar: Order summary */}
                    {!confirmedOrder && <div className="checkout-sidebar">
                        <div className="checkout-summary-card">
                            <h3>Sipariş Toplamı</h3>
                            <div className="checkout-summary-row">
                                <span>Ürünler ({cart.itemCount} adet)</span>
                                <span>{formatPrice(cart.totalAmount)}</span>
                            </div>
                            {couponResult && (
                                <div className="checkout-summary-row checkout-summary-discount">
                                    <span>Kupon ({couponResult.code})</span>
                                    <span>-{formatPrice(couponResult.discount)}</span>
                                </div>
                            )}
                            <div className="checkout-summary-row">
                                <span>Kargo</span>
                                <span style={{color:'var(--success)'}}>Ücretsiz</span>
                            </div>
                            <div className="checkout-summary-total">
                                <span>Toplam</span>
                                <strong>{formatPrice(couponResult ? cart.totalAmount - couponResult.discount : cart.totalAmount)}</strong>
                            </div>
                        </div>
                    </div>}
                </div>
            </div>

            {/* Sözleşme Modal */}
            {agreementModal && (
                <div className="agreement-modal-backdrop" onClick={() => setAgreementModal(null)}>
                    <div className="agreement-modal" onClick={e => e.stopPropagation()}>
                        <div className="agreement-modal-header">
                            <h3>{agreementModal.title}</h3>
                            <button className="agreement-modal-close" onClick={() => setAgreementModal(null)}><X size={20} /></button>
                        </div>
                        <div className="agreement-modal-body" dangerouslySetInnerHTML={{ __html: agreementModal.content }} />
                    </div>
                </div>
            )}
        </div>
    );
}
